import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight, ArrowLeft, Check, Loader2 } from "lucide-react";

export default function Register() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [params] = useSearchParams();
  const planKey = params.get("plan") || "";
  const [plans, setPlans] = useState([]);
  const [selected, setSelected] = useState(planKey);
  const [form, setForm] = useState({ organization: "", name: "", email: "", phone: "", staff: "", password: "", password2: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/public/plans").then(({ data }) => {
      setPlans(data || []);
      if (!planKey && data?.length) setSelected(data[0].lookup_key);
    }).catch(() => setPlans([]));
  }, [planKey]);

  const plan = plans.find((p) => p.lookup_key === selected);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) { toast.error("パスワードは8文字以上にしてください"); return; }
    if (form.password !== form.password2) { toast.error("パスワードが一致しません"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/public/signup", {
        organization: form.organization, name: form.name, email: form.email, phone: form.phone,
        password: form.password, plan: selected, plan_name: plan?.name || "", staff: form.staff,
      });
      localStorage.setItem("wcc_token", data.token);
      await refresh();
      toast.success("アカウントを作成しました。ようこそ！");
      navigate("/");
    } catch (err) { toast.error(apiErr(err)); } finally { setSending(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FAFAF9] to-sky-50/40">
      <header className="border-b border-slate-200/70 bg-white/80 backdrop-blur sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <button onClick={() => navigate("/")} data-testid="register-logo" className="flex items-center gap-2.5">
            <img src="/wcc-icon.png" alt="WCC" className="w-8 h-8 rounded-lg object-contain" />
            <span className="font-serif font-bold text-slate-900">Welfy Care Campus</span>
          </button>
          <button onClick={() => navigate("/login")} data-testid="register-login-link" className="text-sm text-slate-600 hover:text-sky-700 transition-colors">既にアカウントをお持ちの方 →</button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-12 lg:py-16">
        <button onClick={() => navigate("/#pricing")} data-testid="register-back" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 transition-colors mb-8">
          <ArrowLeft className="w-4 h-4" /> 料金プランに戻る
        </button>

        <div className="grid lg:grid-cols-5 gap-8 lg:gap-12 items-start">
          {/* Left: plan selection */}
          <div className="lg:col-span-2">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-sky-700 mb-3 block">無料トライアル登録</span>
            <h1 className="text-3xl sm:text-4xl font-serif font-bold tracking-tight text-slate-900">アカウントを<br />作成しましょう。</h1>
            <p className="mt-4 text-slate-600 text-sm leading-relaxed">登録するとすぐに管理画面にログインし、事業所・職員の登録や研修設定を始められます。</p>

            <div className="mt-8 space-y-3" data-testid="register-plan-list">
              {plans.length === 0 && <div className="text-slate-400 text-sm">プランを読み込み中…</div>}
              {plans.map((p) => {
                const active = selected === p.lookup_key;
                return (
                  <button key={p.lookup_key} type="button" onClick={() => setSelected(p.lookup_key)}
                    data-testid={`register-plan-${p.lookup_key}`}
                    className={`w-full text-left rounded-2xl border p-4 transition-all ${active ? "border-sky-700 bg-sky-50/60 ring-1 ring-sky-700" : "border-slate-200 bg-white hover:border-sky-300"}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-slate-800">{p.name}</span>
                      <span className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${active ? "bg-sky-700 text-white" : "border border-slate-300"}`}>{active && <Check className="w-3 h-3" />}</span>
                    </div>
                    <div className="mt-1 flex items-baseline gap-1">
                      <span className="text-2xl font-serif font-bold text-slate-900">¥{(p.price || 0).toLocaleString()}</span>
                      <span className="text-xs text-slate-500">/ {p.interval}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Right: form */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-3xl border border-slate-200 p-8 lg:p-10 shadow-sm">
              <form onSubmit={submit} className="space-y-4" data-testid="register-form">
                <div>
                  <Label htmlFor="r-org">法人・事業所名 <span className="text-rose-500">*</span></Label>
                  <Input id="r-org" data-testid="register-org" required value={form.organization}
                    onChange={(e) => set("organization", e.target.value)} className="mt-1.5" placeholder="社会福祉法人◯◯" />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="r-name">ご担当者名 <span className="text-rose-500">*</span></Label>
                    <Input id="r-name" data-testid="register-name" required value={form.name}
                      onChange={(e) => set("name", e.target.value)} className="mt-1.5" placeholder="山田 太郎" />
                  </div>
                  <div>
                    <Label htmlFor="r-staff">職員数（任意）</Label>
                    <Input id="r-staff" data-testid="register-staff" value={form.staff}
                      onChange={(e) => set("staff", e.target.value)} className="mt-1.5" placeholder="例：25名" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="r-email">メールアドレス <span className="text-rose-500">*</span></Label>
                    <Input id="r-email" type="email" data-testid="register-email" required value={form.email}
                      onChange={(e) => set("email", e.target.value)} className="mt-1.5" placeholder="you@example.com" />
                    <p className="text-[11px] text-slate-400 mt-1">このメールアドレスがログインIDになります</p>
                  </div>
                  <div>
                    <Label htmlFor="r-phone">電話番号</Label>
                    <Input id="r-phone" data-testid="register-phone" value={form.phone}
                      onChange={(e) => set("phone", e.target.value)} className="mt-1.5" placeholder="03-0000-0000" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="r-pw">パスワード <span className="text-rose-500">*</span></Label>
                    <Input id="r-pw" type="password" data-testid="register-password" required value={form.password}
                      onChange={(e) => set("password", e.target.value)} className="mt-1.5" placeholder="8文字以上" />
                  </div>
                  <div>
                    <Label htmlFor="r-pw2">パスワード（確認） <span className="text-rose-500">*</span></Label>
                    <Input id="r-pw2" type="password" data-testid="register-password2" required value={form.password2}
                      onChange={(e) => set("password2", e.target.value)} className="mt-1.5" placeholder="もう一度入力" />
                  </div>
                </div>

                {plan && (
                  <div className="rounded-xl bg-sky-50/70 border border-sky-100 px-4 py-3 text-sm text-slate-700" data-testid="register-selected-plan">
                    選択中のプラン：<span className="font-bold text-slate-900">{plan.name}</span>（¥{(plan.price || 0).toLocaleString()} / {plan.interval}）
                  </div>
                )}

                <Button type="submit" disabled={sending} data-testid="register-submit"
                  className="w-full rounded-full h-12 bg-sky-700 hover:bg-sky-600 text-white text-base transition-colors">
                  {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <>登録してはじめる <ArrowRight className="w-4 h-4 ml-1.5" /></>}
                </Button>
                <p className="text-[11px] text-slate-400 text-center">登録すると管理画面に自動でログインします。トライアル期間中は無料でお試しいただけます。</p>
              </form>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
